import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"
import { Tooltip, TooltipTrigger, TooltipContent } from "~/react/ui/tooltip"

const suggestionBoxVariants = cva("h-suggestion-box", {
  variants: {
    variant: {
      suggestion: "h-suggestion-box--suggestion",
      loading: "h-suggestion-box--loading",
      update: "h-suggestion-box--update",
    },
  },
  defaultVariants: {
    variant: "suggestion",
  },
})

export interface SuggestionBoxProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof suggestionBoxVariants> {}

const SuggestionBox = React.forwardRef<HTMLDivElement, SuggestionBoxProps>(
  ({ className, variant, children, ...props }, ref) => {
    return (
      <div className={cn(suggestionBoxVariants({ variant, className }))} ref={ref} {...props}>
        <div className="h-suggestion-box__background" />
        {children}
      </div>
    )
  },
)
SuggestionBox.displayName = "SuggestionBox"

interface SuggestionBoxContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const SuggestionBoxContent = React.forwardRef<HTMLDivElement, SuggestionBoxContentProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("h-suggestion-box__content", className)} {...props}>
      {children}
    </div>
  ),
)
SuggestionBoxContent.displayName = "SuggestionBoxContent"

interface SuggestionBoxButtonsProps extends React.HTMLAttributes<HTMLDivElement> {}

const SuggestionBoxButtons = React.forwardRef<HTMLDivElement, SuggestionBoxButtonsProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("h-suggestion-box__buttons", className)} {...props}>
      {children}
    </div>
  ),
)
SuggestionBoxButtons.displayName = "SuggestionBoxButtons"

interface SuggestionBoxLoadingTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

const SuggestionBoxLoadingText = React.forwardRef<HTMLDivElement, SuggestionBoxLoadingTextProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("h-suggestion-box__loading-text", className)} {...props}>
      {children}
    </div>
  ),
)
SuggestionBoxLoadingText.displayName = "SuggestionBoxLoadingText"

interface SuggestionBoxUpdateProps extends React.HTMLAttributes<HTMLDivElement> {
  added?: number
  removed?: number
  changed?: number
  onClose?: () => void
}

const SuggestionBoxUpdate = React.forwardRef<HTMLDivElement, SuggestionBoxUpdateProps>(
  ({ className, added = 0, removed = 0, changed = 0, onClose, ...props }, ref) => {
    // Build message parts dynamically based on what changed using i18n
    const parts: string[] = []
    if (added > 0) {
      parts.push(window.i18n.t("js.discover.suggestion_box.filters_applied", { count: added }))
    }
    if (removed > 0) {
      parts.push(window.i18n.t("js.discover.suggestion_box.filters_removed", { count: removed }))
    }
    if (changed > 0) {
      parts.push(window.i18n.t("js.discover.suggestion_box.filters_changed", { count: changed }))
    }

    const message = parts.join(", ")

    return (
      <div ref={ref} className={cn("h-suggestion-box__update", className)} {...props}>
        <div className="h-suggestion-box__update-text">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="h-suggestion-box__update-text-highlight">{message}</span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs">These filters were automatically applied based on your prompt.</div>
            </TooltipContent>
          </Tooltip>
        </div>
        {onClose && (
          <button type="button" className="h-suggestion-box__update-close" onClick={onClose} aria-label="Close">
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        )}
      </div>
    )
  },
)
SuggestionBoxUpdate.displayName = "SuggestionBoxUpdate"

export { SuggestionBoxContent, SuggestionBoxButtons, SuggestionBoxLoadingText, SuggestionBoxUpdate }
export default SuggestionBox
